export const FORFEO_OBSERVABILITY_CONTRACT_VERSION = "4D-D1" as const;

export type ForfeoRepositoryOwner = "explore" | "giftshop" | "shared";
export type ForfeoCriticality = "critical" | "high" | "standard";
export type ForfeoSignalKind = "counter" | "gauge" | "histogram" | "freshness" | "release_gate";
export type ForfeoObjectiveState = "baseline_required" | "candidate" | "active" | "retired";

export type ForfeoServiceDefinition = {
  id: string;
  label: string;
  owner: ForfeoRepositoryOwner;
  criticality: ForfeoCriticality;
  financialAuthority: ForfeoRepositoryOwner | "none";
  userImpact: string;
};

export type ForfeoSignalDefinition = {
  id: string;
  label: string;
  serviceId: string;
  owner: ForfeoRepositoryOwner;
  kind: ForfeoSignalKind;
  sourceRef: string;
  aggregation: string;
  allowedDimensions: readonly string[];
  forbiddenPayload: readonly string[];
};

export type ForfeoReliabilityObjective = {
  id: string;
  serviceId: string;
  indicator: string;
  goodEvent: string;
  totalEvent: string;
  windowDays: number;
  candidateTarget: number;
  minimumBaselineDays: number;
  state: ForfeoObjectiveState;
  owner: ForfeoRepositoryOwner;
};

export type ForfeoSeverityDefinition = {
  id: "P0" | "P1" | "P2" | "P3";
  label: string;
  acknowledgementMinutes: number;
  updateMinutes: number;
  trigger: string;
  requiredAction: string;
};

export const FORFEO_SERVICE_CATALOG: readonly ForfeoServiceDefinition[] = [
  {
    id: "explore-public-discovery",
    label: "Explore — découverte publique",
    owner: "explore",
    criticality: "high",
    financialAuthority: "none",
    userImpact: "Accès aux régions, expériences et parcours de découverte.",
  },
  {
    id: "explore-admin-operations",
    label: "Explore — console opérationnelle",
    owner: "explore",
    criticality: "high",
    financialAuthority: "none",
    userImpact: "Visibilité administrateur sur les agrégats et dossiers opérationnels.",
  },
  {
    id: "explore-cross-project-sync",
    label: "Explore — synchronisation interprojets",
    owner: "explore",
    criticality: "critical",
    financialAuthority: "none",
    userImpact: "Réception idempotente des résultats canoniques provenant de Giftshop.",
  },
  {
    id: "giftshop-checkout",
    label: "Giftshop — création Checkout",
    owner: "giftshop",
    criticality: "critical",
    financialAuthority: "giftshop",
    userImpact: "Création des sessions transactionnelles officielles.",
  },
  {
    id: "giftshop-payment-webhooks",
    label: "Giftshop — webhooks de paiement",
    owner: "giftshop",
    criticality: "critical",
    financialAuthority: "giftshop",
    userImpact: "Finalisation idempotente des paiements et effets transactionnels.",
  },
  {
    id: "giftshop-refund-delivery",
    label: "Giftshop — remboursements et livraison d’événements",
    owner: "giftshop",
    criticality: "critical",
    financialAuthority: "giftshop",
    userImpact: "Remboursements canoniques et propagation retentable vers Explore.",
  },
  {
    id: "giftshop-pass-validation",
    label: "Giftshop — validation des passes",
    owner: "giftshop",
    criticality: "critical",
    financialAuthority: "giftshop",
    userImpact: "Validation et consommation officielles des QR et passes.",
  },
  {
    id: "shared-release-pipeline",
    label: "Pipeline de livraison partagé",
    owner: "shared",
    criticality: "high",
    financialAuthority: "none",
    userImpact: "Qualité, traçabilité et retour arrière des versions publiées.",
  },
] as const;

export const FORFEO_SIGNAL_CATALOG: readonly ForfeoSignalDefinition[] = [
  {
    id: "edge-request-outcome",
    label: "Résultat agrégé des requêtes Edge",
    serviceId: "shared-release-pipeline",
    owner: "shared",
    kind: "counter",
    sourceRef: "edge.runtime.request_outcome",
    aggregation: "Comptes par fonction, classe HTTP et fenêtre temporelle.",
    allowedDimensions: ["repository", "function", "http_class", "window"],
    forbiddenPayload: ["authorization", "cookie", "request_body", "response_body", "email"],
  },
  {
    id: "edge-latency-bucket",
    label: "Latence Edge agrégée",
    serviceId: "shared-release-pipeline",
    owner: "shared",
    kind: "histogram",
    sourceRef: "edge.runtime.latency_ms",
    aggregation: "Histogrammes p50, p95 et p99 sans trace utilisateur brute.",
    allowedDimensions: ["repository", "function", "window"],
    forbiddenPayload: ["user_id", "email", "ip", "token"],
  },
  {
    id: "admin-auth-rejection",
    label: "Refus d’authentification administrateur",
    serviceId: "explore-admin-operations",
    owner: "shared",
    kind: "counter",
    sourceRef: "_shared/security.requireAdmin",
    aggregation: "Comptes par motif normalisé et fenêtre; aucune adresse courriel retournée.",
    allowedDimensions: ["repository", "reason", "window"],
    forbiddenPayload: ["jwt", "authorization", "email", "ip"],
  },
  {
    id: "operations-case-backlog",
    label: "Dossiers opérationnels ouverts",
    serviceId: "explore-admin-operations",
    owner: "explore",
    kind: "gauge",
    sourceRef: "public.forfeo_operations_cases",
    aggregation: "Comptes par priorité, catégorie et statut.",
    allowedDimensions: ["priority", "category", "status", "window"],
    forbiddenPayload: ["title", "summary", "metadata", "author_email"],
  },
  {
    id: "operations-case-oldest-age",
    label: "Âge du plus ancien dossier actif",
    serviceId: "explore-admin-operations",
    owner: "explore",
    kind: "gauge",
    sourceRef: "public.forfeo_operations_cases.created_at",
    aggregation: "Âge maximal en minutes par priorité.",
    allowedDimensions: ["priority", "window"],
    forbiddenPayload: ["case_id", "title", "summary", "metadata"],
  },
  {
    id: "cross-project-sync-lag",
    label: "Retard de synchronisation Giftshop vers Explore",
    serviceId: "explore-cross-project-sync",
    owner: "shared",
    kind: "freshness",
    sourceRef: "cross_project.event_delivery",
    aggregation: "Écart en secondes entre création canonique et application idempotente.",
    allowedDimensions: ["event_type", "result", "window"],
    forbiddenPayload: ["signature", "nonce", "payload", "customer_id"],
  },
  {
    id: "checkout-creation-outcome",
    label: "Résultat de création Checkout",
    serviceId: "giftshop-checkout",
    owner: "giftshop",
    kind: "counter",
    sourceRef: "checkout.session.creation",
    aggregation: "Comptes succès/échec par type transactionnel.",
    allowedDimensions: ["transaction_type", "result", "window"],
    forbiddenPayload: ["session_id", "customer_id", "email", "amount", "stripe_payload"],
  },
  {
    id: "payment-webhook-freshness",
    label: "Fraîcheur du traitement des webhooks",
    serviceId: "giftshop-payment-webhooks",
    owner: "giftshop",
    kind: "freshness",
    sourceRef: "stripe_webhook.event_processing",
    aggregation: "Latence agrégée entre réception et finalisation idempotente.",
    allowedDimensions: ["event_type", "result", "window"],
    forbiddenPayload: ["stripe_event", "payment_intent", "customer_id", "metadata"],
  },
  {
    id: "refund-outbox-backlog",
    label: "Arriéré de livraison des remboursements",
    serviceId: "giftshop-refund-delivery",
    owner: "giftshop",
    kind: "gauge",
    sourceRef: "refund_delivery_outbox",
    aggregation: "Comptes par état, tentative et âge normalisé.",
    allowedDimensions: ["status", "attempt_bucket", "age_bucket", "window"],
    forbiddenPayload: ["payload", "signature", "refund_id", "order_id", "last_error_raw"],
  },
  {
    id: "refund-delivery-outcome",
    label: "Résultat de livraison des remboursements",
    serviceId: "giftshop-refund-delivery",
    owner: "giftshop",
    kind: "counter",
    sourceRef: "dispatch-explore-refund-events",
    aggregation: "Comptes succès, échecs retentables et échecs terminaux.",
    allowedDimensions: ["result", "attempt_bucket", "window"],
    forbiddenPayload: ["payload", "signature", "nonce", "url", "authorization"],
  },
  {
    id: "pass-validation-outcome",
    label: "Résultat de validation des passes",
    serviceId: "giftshop-pass-validation",
    owner: "giftshop",
    kind: "counter",
    sourceRef: "pass.validation",
    aggregation: "Comptes acceptés/refusés par motif normalisé.",
    allowedDimensions: ["result", "reason", "window"],
    forbiddenPayload: ["qr_payload", "pass_id", "customer_id", "merchant_id"],
  },
  {
    id: "release-health-gate",
    label: "Résultat des portes de livraison",
    serviceId: "shared-release-pipeline",
    owner: "shared",
    kind: "release_gate",
    sourceRef: "release-health",
    aggregation: "Comptes PASS/FAIL par dépôt, suite et version.",
    allowedDimensions: ["repository", "suite", "result", "release"],
    forbiddenPayload: ["environment", "secret", "token", "raw_log"],
  },
] as const;

export const FORFEO_RELIABILITY_OBJECTIVES: readonly ForfeoReliabilityObjective[] = [
  {
    id: "explore-public-availability",
    serviceId: "explore-public-discovery",
    indicator: "Requêtes publiques réussies hors erreurs client explicites.",
    goodEvent: "Réponse utile ou dégradation contrôlée.",
    totalEvent: "Toutes les requêtes publiques admissibles.",
    windowDays: 28,
    candidateTarget: 0.995,
    minimumBaselineDays: 14,
    state: "baseline_required",
    owner: "explore",
  },
  {
    id: "operations-console-availability",
    serviceId: "explore-admin-operations",
    indicator: "Chargements administrateur authentifiés produisant des agrégats valides.",
    goodEvent: "Snapshot complet ou partiel avec sources indisponibles explicitement isolées.",
    totalEvent: "Demandes administrateur autorisées.",
    windowDays: 28,
    candidateTarget: 0.99,
    minimumBaselineDays: 14,
    state: "baseline_required",
    owner: "explore",
  },
  {
    id: "cross-project-sync-freshness",
    serviceId: "explore-cross-project-sync",
    indicator: "Événements canoniques appliqués dans la fenêtre convenue.",
    goodEvent: "Événement idempotent appliqué sans collision dans la fenêtre.",
    totalEvent: "Événements canoniques admissibles reçus.",
    windowDays: 28,
    candidateTarget: 0.995,
    minimumBaselineDays: 14,
    state: "baseline_required",
    owner: "shared",
  },
  {
    id: "checkout-creation-reliability",
    serviceId: "giftshop-checkout",
    indicator: "Créations Checkout officielles acceptées par le chemin serveur.",
    goodEvent: "Session créée ou résultat idempotent déjà existant.",
    totalEvent: "Demandes valides après authentification et validation métier.",
    windowDays: 28,
    candidateTarget: 0.995,
    minimumBaselineDays: 14,
    state: "baseline_required",
    owner: "giftshop",
  },
  {
    id: "payment-webhook-reliability",
    serviceId: "giftshop-payment-webhooks",
    indicator: "Webhooks Stripe authentiques finalisés exactement une fois.",
    goodEvent: "Événement traité ou doublon idempotent confirmé.",
    totalEvent: "Événements Stripe authentiques pris en charge.",
    windowDays: 28,
    candidateTarget: 0.999,
    minimumBaselineDays: 14,
    state: "baseline_required",
    owner: "giftshop",
  },
  {
    id: "refund-delivery-reliability",
    serviceId: "giftshop-refund-delivery",
    indicator: "Remboursements canoniques livrés vers Explore sans perte.",
    goodEvent: "Livraison confirmée ou doublon idempotent confirmé.",
    totalEvent: "Événements de remboursement admissibles dans l’outbox.",
    windowDays: 28,
    candidateTarget: 0.995,
    minimumBaselineDays: 14,
    state: "baseline_required",
    owner: "giftshop",
  },
  {
    id: "pass-validation-reliability",
    serviceId: "giftshop-pass-validation",
    indicator: "Validations légitimes retournant une décision canonique.",
    goodEvent: "Décision signée et cohérente retournée dans la fenêtre.",
    totalEvent: "Demandes légitimes de validation.",
    windowDays: 28,
    candidateTarget: 0.995,
    minimumBaselineDays: 14,
    state: "baseline_required",
    owner: "giftshop",
  },
  {
    id: "release-gate-reliability",
    serviceId: "shared-release-pipeline",
    indicator: "Versions publiées après toutes les portes obligatoires.",
    goodEvent: "Version avec tests, build, anti-secrets et provenance confirmés.",
    totalEvent: "Versions candidates à une publication.",
    windowDays: 28,
    candidateTarget: 1,
    minimumBaselineDays: 14,
    state: "baseline_required",
    owner: "shared",
  },
] as const;

export const FORFEO_SEVERITY_POLICY: readonly ForfeoSeverityDefinition[] = [
  {
    id: "P0",
    label: "Intégrité ou sécurité critique",
    acknowledgementMinutes: 5,
    updateMinutes: 15,
    trigger: "Risque actif d’intégrité financière, compromission ou effets transactionnels non idempotents.",
    requiredAction: "Arrêter le chemin concerné, préserver les preuves et mobiliser les propriétaires Explore et Giftshop.",
  },
  {
    id: "P1",
    label: "Service critique fortement dégradé",
    acknowledgementMinutes: 15,
    updateMinutes: 30,
    trigger: "Échec soutenu d’un parcours critique ou consommation accélérée du budget candidat.",
    requiredAction: "Ouvrir un dossier prioritaire, limiter l’impact et suivre la procédure de reprise.",
  },
  {
    id: "P2",
    label: "Dégradation partielle",
    acknowledgementMinutes: 240,
    updateMinutes: 480,
    trigger: "Dégradation circonscrite avec solution de contournement contrôlée.",
    requiredAction: "Qualifier, assigner et planifier la correction avec preuves avant/après.",
  },
  {
    id: "P3",
    label: "Amélioration de fiabilité",
    acknowledgementMinutes: 1440,
    updateMinutes: 4320,
    trigger: "Signal préventif ou dette de fiabilité sans impact immédiat.",
    requiredAction: "Documenter, prioriser et rattacher à une évolution planifiée.",
  },
] as const;

export const FORFEO_ERROR_BUDGET_POLICY = {
  state: "baseline_required" as const,
  windowDays: 28,
  baselineMinimumDays: 14,
  burnRateWindows: [
    { shortWindowMinutes: 5, longWindowMinutes: 60, multiplier: 14.4, severity: "P1" },
    { shortWindowMinutes: 30, longWindowMinutes: 360, multiplier: 6, severity: "P1" },
    { shortWindowMinutes: 120, longWindowMinutes: 1440, multiplier: 3, severity: "P2" },
  ],
  releaseRules: [
    "Une perte d’intégrité financière bloque toute promotion, indépendamment du budget.",
    "Aucune alerte de burn rate n’est activée avant une baseline suffisante.",
    "Un budget épuisé suspend les changements non essentiels du service concerné.",
    "La reprise exige des preuves mesurées et une note dans le dossier opérationnel.",
  ],
} as const;

export const FORFEO_CORRELATION_CONTRACT = {
  header: "x-forfeo-correlation-id",
  format: "uuid-v4-or-server-opaque-id",
  generatedAt: "first-trusted-ingress",
  propagation: "forward-without-customer-data",
  forbiddenSources: ["email", "user_id", "order_id", "payment_intent", "jwt", "ip"],
  logPolicy: "aggregate-or-redacted-only",
} as const;

export const FORFEO_TELEMETRY_GUARDRAILS = {
  defaultDataClass: "aggregate_only",
  serviceRoleLocation: "server_only",
  rawPayloadStorage: "forbidden",
  secretStorage: "forbidden",
  financialMutation: "forbidden",
  maximumDimensionsPerSignal: 6,
  retentionState: "pending_legal_and_operational_approval",
  productionMeasurementsState: "not_collected_by_4d_d1",
} as const;

export function findForfeoService(serviceId: string) {
  return FORFEO_SERVICE_CATALOG.find((service) => service.id === serviceId) || null;
}

export function findForfeoSignal(signalId: string) {
  return FORFEO_SIGNAL_CATALOG.find((signal) => signal.id === signalId) || null;
}
